import {
  enumPurchaseLoadSortOption,
  enumPurchaseLoadStatus,
  PurchaseLoadSortOption,
  WorkOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import {
  SortingQueueFlattenedItem,
  sortingQueueQueryAtom,
} from '../_graphql/querySortingQueue';

import { atomFromQueryString } from '~/shared/state';

export type SortingStatusFilter =
  | 'all'
  | 'pending'
  | 'inProgress'
  | 'completed';

export const $selectedFacility = atomFromQueryString.string('f');
export const $limit = atomFromQueryString.number('limit', 50);
export const $offset = atomFromQueryString.number('offset', 0);
export const $sortingStatusFilter =
  atomFromQueryString.string<SortingStatusFilter>('status', 'all');
export const $currentSorting = atom<PurchaseLoadSortOption[]>([
  enumPurchaseLoadSortOption.ReceivedDateDesc,
]);

export const $remoteSortingQueue = sortingQueueQueryAtom((get) => {
  const selectedFacility = get($selectedFacility);
  const limit = get($limit);
  const offset = get($offset);
  const sortingParams = get($currentSorting);

  const params: {
    facilityId?: string;
    limit: number;
    offset: number;
    sorting: PurchaseLoadSortOption[];
    status: (typeof enumPurchaseLoadStatus)[keyof typeof enumPurchaseLoadStatus][];
  } = {
    limit,
    offset,
    sorting: sortingParams,
    status: [enumPurchaseLoadStatus.InventoryComplete],
  };

  if (selectedFacility && selectedFacility !== 'all') {
    params.facilityId = selectedFacility;
  }

  return {
    variables: params,
  };
});

export const $filteredSortingQueueItems = atom((get) => {
  const remoteData = get($remoteSortingQueue);
  const statusFilter = get($sortingStatusFilter);

  if (!remoteData.data) {
    return [];
  }

  const items = remoteData.data.items;

  if (statusFilter === 'all') {
    return items;
  }

  return items.filter((item: SortingQueueFlattenedItem) => {
    const workOrderStatus = item.sortingWorkOrderStatus;

    if (statusFilter === 'pending') {
      return !workOrderStatus || workOrderStatus === 'Draft';
    }

    if (statusFilter === 'inProgress') {
      return (
        workOrderStatus === ('Open' as WorkOrderStatus) ||
        workOrderStatus === ('InProgress' as WorkOrderStatus)
      );
    }

    if (statusFilter === 'completed') {
      return workOrderStatus === ('Completed' as WorkOrderStatus);
    }

    return true;
  });
});

export const $sortingQueueCounts = atom((get) => {
  const remoteData = get($remoteSortingQueue);

  if (!remoteData.data) {
    return { pending: 0, inProgress: 0, completed: 0, total: 0 };
  }

  return {
    ...remoteData.data.counts,
    total: remoteData.data.totalCount,
  };
});
